import React, { Dispatch, memo, SetStateAction, useCallback } from 'react';
import { Control, Controller, FieldError, FieldPath, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { UseFieldArrayUpdate } from 'react-hook-form';
import { Dropdown } from '@tcc/crate';
import { getMessage } from 'util/locale';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveCropDfpUuid } from 'current/selectors';
import { Product } from 'store/types/types';
import { FormData } from '../../types';
import { copyValuesFromMatchingHybrid, loadHybridOptions } from '../utils';
import { addErrorToast } from 'messages';

interface HybridDropdownCellProps {
  control: Control<FormData>;
  setValue: UseFormSetValue<FormData>;
  getValues: UseFormGetValues<FormData>;
  update: UseFieldArrayUpdate<FormData, 'fields'>;
  name: FieldPath<FormData>;
  usedHybrids: Record<string, Product[]>;
  setUsedHybrids: Dispatch<SetStateAction<Record<string, Product[]>>>;
  fieldCountryCode: string;
  rowIndex: number;
  onRecommendationClear?: () => void;
  disabled?: boolean;
  excludedBrandUuids?: string[];
}

export const HybridDropdownCell = memo(function Cell({
  control,
  setValue,
  getValues,
  update,
  name,
  usedHybrids,
  setUsedHybrids,
  fieldCountryCode,
  rowIndex,
  onRecommendationClear,
  disabled,
  excludedBrandUuids,
}: HybridDropdownCellProps) {
  const dispatch = useDispatch();
  const cropDfpUuid = useSelector(getActiveCropDfpUuid);

  const setValueWithValidation = useCallback((
    name: Parameters<typeof setValue>[0],
    value: Parameters<typeof setValue>[1]
  ) => {
    setValue(name, value, { shouldValidate: true });
  }, [setValue]);

  const loadOptions = useCallback((input: string, onOptionsLoaded: (options: unknown[]) => void) => {
    const onError = () => {
      dispatch(addErrorToast(getMessage('help-text__failure--search')));
      onOptionsLoaded([]);
    };

    loadHybridOptions({
      input, usedHybrids, fieldCountryCode,
      cropDfpUuid, onOptionsLoaded, onError, excludedBrandUuids,
    });
  }, [fieldCountryCode, cropDfpUuid, usedHybrids, dispatch, excludedBrandUuids]);

  const onHybridSelect = useCallback((selectedHybrid) => {
    setUsedHybrids((prevState) => {
      const newState = prevState[fieldCountryCode]?.filter(({ id }) => id !== selectedHybrid.id) || [];
      newState.unshift(selectedHybrid);
      return {
        ...prevState,
        [fieldCountryCode]: newState
      };
    });

    copyValuesFromMatchingHybrid({
      formFields: getValues()?.fields || [],
      selectedHybrid,
      currentRowIndex: rowIndex,
      setValue: setValueWithValidation
    });
  }, [setUsedHybrids, fieldCountryCode, rowIndex, setValueWithValidation, getValues]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        return (
          <Dropdown
            width="100%"
            disabled={disabled}
            placeholder={getMessage('bulk-fvss-review-scripts-hybrid-placeholder')}
            value={(field.value as Product)?.name}
            setValue={(_: string, data: Product) => {
              if ((field.value as Product)?.id !== data.id) {
                onHybridSelect(data);
                field.onChange(data || '');
                const currentFieldData = getValues(`fields.${rowIndex}`);
                if (currentFieldData?.recommendation) {
                  update(rowIndex, {
                    ...currentFieldData,
                    hybrid: data,
                    recommendation: undefined,
                    selectedRecommendation: undefined,
                  });
                  onRecommendationClear?.();
                }
              }
            }}
            loadOptions={loadOptions}
            type="AsyncDropdown"
            error={(error as unknown as { id: FieldError })?.id?.message}
            className="script-generation-row-input"
          />
        );
      }}
    />
  );
});
